/**
 * BlogRssFeedsPanel — admin panel that lists every per-category RSS
 * feed URL with a one-click copy button. Lives at the top of the Blog
 * Manager so the admin can grab the right feed URL to plug into
 * Mailchimp / Substack / Feedly / Zapier without leaving the page.
 *
 * Public feed routes (no auth needed) — both the all-posts feed and
 * the per-category variants come from `/api/rss/blog/feeds.json`.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Rss, Copy, ChevronDown, ChevronRight, Check, ExternalLink, RefreshCw } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';

const BlogRssFeedsPanel = () => {
  const [open, setOpen] = useState(false);
  const [feeds, setFeeds] = useState([]);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // Bypass any Cloudflare/browser cache so admins always see the
    // latest set after adding a new category or publishing a post.
    const cacheBuster = Date.now();
    const { data, error } = await apiFetch(
      `/api/rss/blog/feeds.json?_=${cacheBuster}`,
    );
    setLoading(false);
    if (error || !data?.feeds) return;
    setFeeds(data.feeds);
  }, []);

  // Fetch on first open and refresh every time the panel re-opens so
  // count badges stay accurate after a publish/unpublish cycle.
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const copy = async (slug, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug || 'all');
      setTimeout(() => setCopiedSlug(null), 1800);
      toast.success('Feed URL copied to clipboard.');
    } catch {
      toast.error('Could not copy. Long-press the URL and copy manually.');
    }
  };

  // Subtract 1 from the badge count because the first item is the
  // "All posts" mega-feed, not a per-category one.
  const categoryFeedCount = Math.max(0, feeds.length - 1);

  return (
    <Card className="border-orange-500/30 bg-orange-500/5" data-testid="blog-rss-feeds-panel">
      <CardContent className="py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-2 text-left"
          data-testid="blog-rss-feeds-toggle"
          aria-expanded={open}
        >
          {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          <Rss className="h-4 w-4 text-orange-500 shrink-0" />
          <span className="text-sm font-medium">RSS feeds for newsletters &amp; schedulers</span>
          {feeds.length > 0 && (
            <Badge
              variant="secondary"
              className="ml-auto text-[10px]"
              data-testid="blog-rss-feeds-count"
            >
              All + {categoryFeedCount} categor{categoryFeedCount === 1 ? 'y' : 'ies'}
            </Badge>
          )}
        </button>

        {open && (
          <div className="mt-3 space-y-2 pl-1" data-testid="blog-rss-feeds-list">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                Plug any of these URLs into Substack/Mailchimp/Feedly/Zapier to auto-share new posts.
                Per-category feeds are great for topic-narrow audiences.
              </p>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 shrink-0"
                onClick={load}
                disabled={loading}
                title="Refresh feed list"
                data-testid="blog-rss-feeds-refresh"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {feeds.length === 0 ? (
              <p className="text-xs text-muted-foreground italic" data-testid="blog-rss-feeds-loading">
                {loading ? 'Loading…' : 'No feeds available yet — publish a post to generate one.'}
              </p>
            ) : (
              feeds.map((f) => (
                <div
                  key={f.slug || 'all'}
                  className="flex items-center gap-2 p-2 rounded-md bg-background border break-all"
                  data-testid={`rss-feed-${f.slug || 'all'}`}
                >
                  <Rss className="h-3.5 w-3.5 text-orange-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {f.label} <span className="text-xs text-muted-foreground">({f.count})</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground break-all font-mono">
                      {f.url}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 shrink-0"
                    onClick={() => copy(f.slug, f.url)}
                    title="Copy feed URL"
                    data-testid={`rss-copy-${f.slug || 'all'}`}
                  >
                    {copiedSlug === (f.slug || 'all') ? (
                      <Check className="h-3.5 w-3.5 text-green-500" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-7 px-2 inline-flex items-center text-muted-foreground hover:text-foreground shrink-0"
                    title="Open feed in a new tab"
                    data-testid={`rss-open-${f.slug || 'all'}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BlogRssFeedsPanel;
